<template>
  <div class="SearchTripSummary" :class="{ 'is-expanded': isExpanded }">
    <header class="SearchTripSummary-header">
      <I18nT keypath="ui.booking.search.summary.title" tag="span" class="SearchTripSummary-destination">
        <template #destination>
          <Icon
            v-if="showAnywhereIcon"
            icon="creation"
            class="SearchTripSummary-anywhereIcon"
          />
          {{ destinationName }}
        </template>
      </I18nT>

      <span class="SearchTripSummary-details">
        {{ t(`ui.global.tripType.${tripType}`) }} | {{ departDateFormatted }} {{ returnDateFormatted ? `- ${returnDateFormatted}` : '' }}
      </span>

      <IconButton
        v-if="isMobile"
        class="SearchTripSummary-action"
        size="md"
        :icon="actionIcon"
        :ariaLabel="actionLabel"
        @click="handleEditClick"
      />

      <ActionButton
        v-else
        class="SearchTripSummary-action"
        variant="tertiary"
        size="md"
        :leftIcon="actionIcon"
        @click="handleEditClick"
      >
        {{ actionLabel }}
      </ActionButton>
    </header>

    <Transition v-show="isExpanded" name="fade">
      <slot />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { type TripType, TripTypes } from '@cvo/domain';
import { format } from 'date-fns';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { useResponsive, useStation } from '@cvo/ui/composables';
import ActionButton from '../../../atoms/ActionButton/ActionButton.vue';
import IconButton from '../../../atoms/IconButton/IconButton.vue';
import Icon from '../../../atoms/Icon/Icon.vue';
import type { SearchTripSummaryProps, SearchTripSummaryEmits } from './SearchTripSummary.model';

const props = withDefaults(defineProps<SearchTripSummaryProps>(), {
  tripType: TripTypes.OW,
});

const emit = defineEmits<SearchTripSummaryEmits>();

const { isMobile } = useResponsive();
const { t, tf } = useI18n();

const isExpanded = defineModel<boolean>();

const { stationName: destinationName } = useStation(() => props.destination);

const departDateFormatted = computed<string>(() => {
  return props.departureDate ? format(props.departureDate, t('ui.date.formats.dayMonth')) : '';
});

const returnDateFormatted = computed<string | undefined>(() => {
  return props.returnDate ? format(props.returnDate, t('ui.date.formats.dayMonth')) : undefined;
});

const actionLabel = computed(() => {
  return isExpanded.value ? t('ui.booking.search.closeEdit') : t('ui.booking.search.edit');
});

const actionIcon = computed(() => {
  return isExpanded.value ? 'window-close' : 'pencil';
});

function handleEditClick() {
  isExpanded.value = !isExpanded.value;

  if (isExpanded.value) {
    emit('expand');
  }
};
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .SearchTripSummary {
    padding-bottom: vars.$dimensions-d-3;
    color: vars.$molecules-searchtrip-header-text-color;

    &-header {
      width: 100%;
      padding: vars.$molecules-searchtrip-header-lg-md-padding-top-bottom vars.$molecules-searchtrip-header-lg-md-padding-left-right;

      // Grid layout
      display: grid;
      grid-template-columns: auto 1fr auto;
      grid-gap: vars.$dimensions-d-2;
      align-items: center;

      color: vars.$molecules-searchtrip-header-text-color;
      @include text.heading(h5);

      .SearchTripSummary-details {
        color: vars.$molecules-searchtrip-header-text-color;
      }

      @include responsive.mobile {
        @include text.heading(h6);

        padding: vars.$molecules-searchtrip-header-sm-padding-top-bottom vars.$molecules-searchtrip-header-sm-padding-left-right;

        grid-template-columns: minmax(0, 1fr) auto;
        grid-template-rows: 1fr 1fr;
        align-items: center;

        .SearchTripSummary-details {
          grid-row: 2;
          grid-column: 1 / -1;
        }

        .SearchTripSummary-action {
          grid-column: 2;
          justify-self: end;
        }
      }
    }

    &-destination {
      grid-column: 1;
      min-width: 0;
      margin-inline-end: vars.$dimensions-d-2;
    }

    &-anywhereIcon {
      vertical-align: middle;
    }

    &-details {
      grid-column: 2;
      color: vars.$organisms-tripsummary-text-secondary-color;
      @include text.text(md);
      margin-inline-end: vars.$dimensions-d-2;
    }

    &-action {
      grid-column: 3;
    }

    &:not(.is-expanded) .SearchTripSummary-header {
      border-bottom: vars.$organisms-tripsummary-border-stroke solid vars.$organisms-tripsummary-border-color;
    }
  }
}
</style>